import {Component, OnDestroy, OnInit} from '@angular/core';
import {
  IPlatformToolComponent,
  IPlatformToolHandler
} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommand, PlatformCommandResponse} from '@shared/commands/command-base';
import {FormBuilder, FormGroup} from '@angular/forms';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';
import {AdminPanelState} from '@features/admin-panel/store/reducers/admin-panel.reducer';
import {getSelectedCompany} from '@features/admin-panel/store/selectors/admin-panel.selector';
import {GetCompanyDetailAction} from '@features/admin-panel/store/actions/admin-panel.action';
import {Api} from '@shared/constants/common-field.constants';

@Component({
  standalone: false,
  selector: 'app-admin-panel-company-detail-form',
  templateUrl: './admin-panel-company-detail-form.component.html',
  styleUrls: ['./admin-panel-company-detail-form.component.scss']
})
export class AdminPanelCompanyDetailFormComponent implements OnInit, OnDestroy, IPlatformToolComponent {

  handler: IPlatformToolHandler;
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  selectedTab: TabModel;

  detailForm: FormGroup;
  company: any;
  isEditing: {companyName: boolean; description: boolean; domain: boolean} = {
    companyName: false,
    description: false,
    domain: false
  };

  private unsubscribe$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AdminPanelState>
  ) {}

  ngOnInit(): void {
    this.store.pipe(select(getSelectedCompany), takeUntil(this.unsubscribe$)).subscribe(c => {
      this.company = c;
      if (this.company) {
        this.buildForm();
        const editable = this.isEditable();
        this.setAddUserButtonDisabled(!editable);
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  isEditable(): boolean {
    const source = this.company.source ?? '';
    return source.toLowerCase().includes('orbitax') 
    || (source.toLowerCase().includes('self') && this.company.registeredBy.toLowerCase().includes('@orbitax.com'));
  }

  onLoad(): void {
    const companyId = this.selectedTab?.tabData?.['CompanyId'];
    if (companyId) {
      this.store.dispatch(new GetCompanyDetailAction(companyId));
    }
    // Handle case where store already had cached data and fired before magicLinkModel was set
    if (this.company) {
      const editable = this.isEditable();
      this.setAddUserButtonDisabled(!editable);
    }
  }

  onAfterExecuteNextCommand(nextCommandResponse: PlatformCommandResponse): void {}
  onAfterExecutePreviousCommand(previousCommandResponse: PlatformCommandResponse): void {}
  onBeforeExecuteNextCommand(): boolean { return false; }
  onBeforeExecutePreviousCommand(): boolean { return false; }

  private buildForm(): void {
    const source = (this.company.source ?? '').toLowerCase();
    this.detailForm = this.formBuilder.group({
      companyId: [this.company.companyId ?? ''],
      companyName: [this.company.companyName ?? ''],
      description: [this.company.description ?? ''],
      source: [this.company.source ?? ''],
      domain: [this.company.domain ?? ''],
      isActive: [this.company.isActive]
    });

    this.detailForm.disable();
    if (source.includes('orbitax')) {
      this.detailForm.controls['companyName'].enable();
      this.detailForm.controls['description'].enable();
      this.detailForm.controls['domain'].enable();
      this.detailForm.controls['isActive'].enable();
    }
  }

  editField(field: string): void {
    this.resetEditing();
    this.isEditing[field] = true;
  }

  saveFieldData(event: any, field: string): void {
    this.detailForm.controls[field].setValue(event.text);
    this.resetEditing();
    this.updateCompanyData();
  }

  resetEditing(): void {
    this.isEditing = {companyName: false, description: false, domain: false};
  }

  private setAddUserButtonDisabled(disabled: boolean): void {
    const items: any[] = this.magicLinkModel?.breadcrumbBar?.items ?? [];
    for (const item of items) {
      const cmd = (item.commands ?? []).find((c: any) => c.name === 'AddUserPlatformCommand');
      if (cmd) {
        cmd.isDisabled = disabled;
        return;
      }
    }
  }

  updateCompanyData(): void {
    const command = new PlatformCommand('UpdateCompanyPlatformCommand', Api.AdminPanel);
    command.setValue('Company', {
      companyId: this.company.companyId,
      companyName: this.detailForm.controls['companyName'].value?.trim(),
      description: this.detailForm.controls['description'].value?.trim(),
      domain: this.detailForm.controls['domain'].value?.trim(),
      isActive: this.detailForm.controls['isActive'].value,
      registrationDate: this.company.registrationDate,
      registeredBy: this.company.registeredBy,
      source: this.company.source
    });
    this.handler?.executeCommand(command);
  }
}
